import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    User,
    MapPin,
    Calendar,
    ShieldCheck,
    Shield,
    Clock,
    Server,
    Hash,
    Crown,
    AlertTriangle,
    CheckCircle2,
    Ban,
    Sparkles,
    History as HistoryIcon,
    Briefcase,
    Activity,
    ChevronDown,
    ChevronUp,
    AlertTriangle as AlertOctagon, // Using AlertTriangle as fallback for safety
    FileText
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { usePunishment } from '../../contexts/PunishmentContext';
import { useTickets } from '../../contexts/TicketContext';
import { useDynamicGradient } from '../../hooks/useDynamicGradient';
import PunishmentAppealModal from './PunishmentAppealModal';
import RevocationHistoryModal from './RevocationHistoryModal';
import { useNavigate } from 'react-router-dom';

// Importando imagens das cidades
import capitalImg from '../../assets/cities/capital.png';
import valleySulImg from '../../assets/cities/valley_sul.png';
import valleyNorteImg from '../../assets/cities/valley_norte.png';
import moradaImg from '../../assets/cities/morada.png';

const UserCharacters = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { getActiveCharacterPunishments, getPunishmentsByCharacter, getActivePunishments } = usePunishment();
    const { tickets } = useTickets();
    const dynamicGradient = useDynamicGradient();
    const [selectedPunishment, setSelectedPunishment] = useState(null);
    const [isAppealModalOpen, setIsAppealModalOpen] = useState(false);
    const [isRevokeHistoryOpen, setIsRevokeHistoryOpen] = useState(false);
    const [selectedCharacterRevocations, setSelectedCharacterRevocations] = useState([]);
    const [expandedPunishmentId, setExpandedPunishmentId] = useState(null);

    // Função para formatar data
    const formatDate = (dateString, includeTime = false) => {
        if (!dateString) return 'N/A';
        const options = {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        };

        if (includeTime) {
            options.hour = '2-digit';
            options.minute = '2-digit';
        }

        return new Date(dateString).toLocaleString('pt-BR', options);
    };

    // Configuração visual por cidade
    const getCityTheme = (city) => {
        const themes = {
            'CAPITAL': { image: capitalImg, color: 'blue' },
            'VALLEY NORTE': { image: valleyNorteImg, color: 'purple' },
            'VALLEY SUL': { image: valleySulImg, color: 'orange' },
            'MORADA DO VALLEY': { image: moradaImg, color: 'emerald' }
        };
        return themes[city] || { image: null, color: 'gray' };
    };

    // Helper to find appeal ticket for a punishment
    const getAppealTicketId = (punishment) => {
        if (punishment.appealTicketId) return punishment.appealTicketId;

        // Retroactive lookup: find ticket with metadata.punishmentId matching this punishment
        const foundTicket = tickets.find(t =>
            t.type === 'APPEAL' &&
            t.metadata &&
            t.metadata.punishmentId === punishment.id
        );

        return foundTicket ? foundTicket.id : null;
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        show: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        show: { opacity: 1, y: 0 }
    };

    // Helper para normalizar strings (remover acentos e colocar em CAPS)
    const normalizeStr = (str) => {
        if (!str) return '';
        return String(str).trim().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase();
    };

    // Ordem de severidade das punições
    const PUNISHMENT_SEVERITY_ORDER = [
        'BANIMENTO PERMANENTE',
        'SUSPENSAO TEMPORARIA',
        'ADVERTENCIA',
        'AMARRACAO',
        'RESTRINCAO DE PERSONAGEM',
        'REMOCAO DE WHITELIST',
        'INTIMACAO',
        'ALINHAMENTO',
        'SOLICITAR CONTATO'
    ];

    const sortPunishments = (punishments) => {
        return [...punishments].sort((a, b) => {
            const indexA = PUNISHMENT_SEVERITY_ORDER.indexOf(normalizeStr(a.punishmentType));
            const indexB = PUNISHMENT_SEVERITY_ORDER.indexOf(normalizeStr(b.punishmentType));

            // Se tipo não estiver na lista, joga pro final
            const valA = indexA === -1 ? 999 : indexA;
            const valB = indexB === -1 ? 999 : indexB;

            return valA - valB;
        });
    };

    // Check for global ticket ban
    const ticketBan = user?.discordId
        ? getActivePunishments(user.discordId).find(p => p.punishmentType === 'Blacklist de Tickets')
        : null;

    const getCharacterStatus = (activePunishments) => {
        if (!activePunishments || activePunishments.length === 0) {
            return {
                label: 'REGULAR',
                color: 'emerald',
                icon: ShieldCheck,
                description: "Sua conduta é exemplar. Continue seguindo as regras para manter o acesso total a todas as funcionalidades da cidade."
            };
        }

        const normalizedTypes = activePunishments.map(p => normalizeStr(p.punishmentType));

        // SUSPENSÃO: BANIMENTO PERMANENTE ou SUSPENSAO TEMPORARIA
        if (normalizedTypes.some(t => t.includes('BANIMENTO') || t.includes('SUSPENSAO'))) {
            return {
                label: 'SUSPENSÃO',
                color: 'red',
                icon: AlertOctagon,
                description: "Conta suspensa temporariamente por violação grave ou acúmulo de advertências. Acesso ao servidor bloqueado até a expiração do prazo."
            };
        }

        // ADVERTENCIA: ADVERTENCIA ou AMARRACAO
        if (normalizedTypes.some(t => t.includes('ADVERTENCIA') || t.includes('AMARRACAO'))) {
            return {
                label: 'ADVERTÊNCIA',
                color: 'yellow',
                icon: AlertTriangle,
                description: "Avisos formais por violações leves. Atenção: O acúmulo de 3 advertências em um período de 90 dias resultará em banimento automático. Violações graves podem sofrer punição direta sem avisos."
            };
        }

        // TODO RESTO: RESTRITO
        return {
            label: 'RESTRITO',
            color: 'blue',
            icon: Shield,
            description: "Sua conta possui restrições administrativas ativas (ex: Intimação, Remoção de Whitelist). Você está sob observação ou aguardando algum procedimento da Staff."
        };
    };

    return (
        <div className="min-h-full bg-gradient-to-br from-black via-gray-900 to-blue-900 animate-gradient-slow text-white p-6 rounded-3xl space-y-8">
            <div className="flex flex-col gap-2">
                <h1 className="text-4xl font-bold flex items-center tracking-tight">
                    <div className="p-3 bg-blue-500/10 rounded-2xl mr-4 border border-blue-500/20">
                        <User className="h-8 w-8 text-blue-400" />
                    </div>
                    <span className={`bg-gradient-to-r ${dynamicGradient} bg-[length:200%_auto] bg-clip-text text-transparent animate-gradient-x`}>Painel de Identidade</span>
                </h1>
                <p className="text-gray-400 text-lg ml-1">
                    Visão geral de seus personagens e status na cidade.
                </p>
            </div>


            {/* Ticket Ban Status Card */}
            {/* Ticket Ban Status Card */}
            {ticketBan && (
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-red-500/10 border border-red-500/20 rounded-2xl p-6 relative overflow-hidden"
                >
                    <div className="absolute top-0 right-0 p-32 bg-red-500/5 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none"></div>

                    <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center gap-6">
                        <div className="h-16 w-16 bg-red-500/20 rounded-2xl flex items-center justify-center shrink-0 border border-red-500/20">
                            <Ban className="h-8 w-8 text-red-500" />
                        </div>

                        <div className="flex-1 space-y-2">
                            <div className="flex items-center gap-3">
                                <h2 className="text-xl font-bold text-white">Acesso a Tickets Bloqueado</h2>
                                {(() => {
                                    const appealId = getAppealTicketId(ticketBan);
                                    if (appealId) {
                                        // If ticket exists, check if it's closed (denied if pun is still active)
                                        // We assume if punishment is active and ticket is closed, it was denied.
                                        // But we lack ticket status here directly. Let's rely on punishment status if updated, or just presence of ticket.
                                        // Ideally PunishmentContext updates punishment status to 'under_review' when appeal is made.

                                        if (ticketBan.status === 'under_review' || ticketBan.status === 'active') {
                                            // Check actual ticket status if possible, or assume under review if recent
                                            // For simple UI:
                                            return (
                                                <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 animate-pulse">
                                                    EM ANÁLISE
                                                </Badge>
                                            )
                                        }
                                    }
                                    return (
                                        <Badge className="bg-red-500 text-white border-0">
                                            BLACKLIST
                                        </Badge>
                                    )
                                })()}

                            </div>
                            <p className="text-gray-300 max-w-3xl">
                                Sua conta está temporariamente impedida de abrir novos tickets.
                                Esta é uma medida administrativa aplicada globalmente ao seu usuário.
                            </p>

                            <div className="flex flex-wrap items-center gap-4 mt-4 pt-2">
                                <div className="flex items-center gap-2 text-sm text-red-200 bg-red-500/10 px-3 py-1.5 rounded-lg border border-red-500/10">
                                    <AlertTriangle className="h-4 w-4" />
                                    <span className="font-bold">Motivo:</span>
                                    <span>{ticketBan.publicReason || 'Violação das diretrizes'}</span>
                                </div>

                                <div className="flex items-center gap-2 text-sm text-red-200 bg-red-500/10 px-3 py-1.5 rounded-lg border border-red-500/10">
                                    <Clock className="h-4 w-4" />
                                    <span className="font-bold">Expira em:</span>
                                    <span>{ticketBan.expiresAt ? formatDate(ticketBan.expiresAt, true) : 'Indeterminado'}</span>
                                </div>

                                {/* Action Buttons - Inline */}
                                {(() => {
                                    const appealId = getAppealTicketId(ticketBan);

                                    if (!appealId && ticketBan.status === 'active') {
                                        return (
                                            <Button
                                                onClick={() => {
                                                    setSelectedPunishment({
                                                        ...ticketBan,
                                                        characterId: null,
                                                        characterName: null
                                                    });
                                                    setIsAppealModalOpen(true);
                                                }}
                                                className="bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-500 hover:to-rose-500 text-white border-none shadow-lg shadow-red-500/20 transition-all hover:scale-[1.02] h-8 px-4 text-sm"
                                            >
                                                Recorrer da Decisão
                                            </Button>
                                        );
                                    } else if (appealId) {
                                        return (
                                            <div className="flex gap-3 items-center">
                                                <Button
                                                    onClick={() => navigate(`/tickets/${appealId}`)}
                                                    className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white border-none shadow-lg shadow-orange-500/20 transition-all hover:scale-[1.02] h-8 px-4 text-sm"
                                                >
                                                    <FileText className="mr-2 h-4 w-4" />
                                                    Ver Ticket
                                                </Button>

                                                {(ticketBan.status === 'active' && appealId) && (
                                                    <div className="flex items-center px-3 py-1.5 rounded-lg bg-red-950/30 border border-red-500/20 text-red-200 text-sm font-bold">
                                                        Decisão Final
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    }
                                    return null;
                                })()}
                            </div>
                        </div>
                    </div>
                </motion.div>
            )}


            <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="show"
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            >
                {user?.cities && user.cities.length > 0 ? (
                    user.cities
                        .filter(city => !['pending_approval', 'pending'].includes(city.status))
                        .map((character, index) => {
                            const cityTheme = getCityTheme(character.city);
                            const allRaw = getActiveCharacterPunishments(character.playerId, user?.discordId, character.city) || [];
                            const notificationTypes = ['Amarração', 'Intimação', 'Alinhamento', 'Solicitar Contato'];
                            const rawPunishments = allRaw.filter(p => !notificationTypes.includes(p.punishmentType));
                            const activePunishments = sortPunishments(rawPunishments);

                            const charStatus = getCharacterStatus(activePunishments);
                            const isClean = activePunishments.length === 0;

                            const allCharacterPunishments = getPunishmentsByCharacter(character.playerId, user?.discordId, character.city) || [];
                            const revokedPunishments = allCharacterPunishments.filter(p => p.status === 'revoked');
                            const hasRevocations = revokedPunishments.length > 0;

                            // Dynamic Styles based on status
                            const statusColor = charStatus.color;
                            const statusGlow = `shadow-[0_0_30px_-5px_rgba(${statusColor === 'emerald' ? '16,185,129' : statusColor === 'red' ? '239,68,68' : statusColor === 'yellow' ? '234,179,8' : '59,130,246'},0.15)]`;
                            const borderColor = `border-${statusColor}-500/30`;
                            const StatusIcon = charStatus.icon;

                            return (
                                <motion.div key={index} variants={itemVariants} className="group h-full">
                                    <Card className={`bg-[#0f1115] border ${borderColor} ${statusGlow} transition-all duration-300 relative overflow-hidden h-full flex flex-col hover:border-opacity-50`}>

                                        {/* Background Image Overlay */}
                                        {cityTheme.image && (
                                            <div className="absolute top-0 inset-x-0 h-32 opacity-20">
                                                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#0f1115]" />
                                                <img src={cityTheme.image} alt="" className="w-full h-full object-cover grayscale mix-blend-overlay" />
                                            </div>
                                        )}

                                        {/* Header Section */}
                                        <div className="p-6 relative z-10 flex flex-col gap-6">

                                            {/* Top Status Indicator with Tooltip */}
                                            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[50] group/status shrink-0">
                                                <div className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full border bg-[#0f1115] border-${statusColor}-500/30 text-${statusColor}-400 ${statusColor !== 'emerald' ? 'animate-pulse' : ''} cursor-help transition-all hover:bg-${statusColor}-500/20 shadow-xl shadow-${statusColor}-500/10`}>
                                                    <StatusIcon className="h-4 w-4" />
                                                    <span className="text-[11px] font-extrabold uppercase tracking-widest">{charStatus.label}</span>
                                                </div>

                                                {/* Status Intelligence Tooltip */}
                                                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-64 p-4 rounded-xl bg-[#1a1c23] border border-white/10 shadow-2xl opacity-0 invisible group-hover/status:opacity-100 group-hover/status:visible transition-all duration-300 z-[100] transform origin-top scale-95 group-hover/status:scale-100">
                                                    <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-[#1a1c23] border-l border-t border-white/10 rotate-45" />
                                                    <div className="relative space-y-2 text-center">
                                                        <div className="flex items-center justify-center gap-2">
                                                            <div className={`p-1.5 rounded-lg bg-${statusColor}-500/20 text-${statusColor}-400`}>
                                                                <StatusIcon className="h-4 w-4" />
                                                            </div>
                                                            <span className={`text-xs font-bold text-${statusColor}-400 uppercase tracking-widest`}>Status: {charStatus.label}</span>
                                                        </div>
                                                        <p className="text-[11px] text-gray-300 leading-relaxed font-medium">
                                                            {charStatus.description}
                                                        </p>
                                                        {statusColor === 'yellow' && (
                                                            <div className="pt-2 border-t border-white/5">
                                                                <div className="flex items-center justify-center gap-1.5 text-[10px] text-orange-400 font-bold uppercase">
                                                                    <AlertTriangle className="h-3 w-3" />
                                                                    <span>Regra de Acúmulo</span>
                                                                </div>
                                                                <p className="text-[10px] text-gray-500 mt-1 italic">
                                                                    3 Advertências em 90 dias = Desligamento automático.
                                                                </p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Header Info */}
                                            <div className="flex justify-between items-start gap-4">
                                                <div className="flex gap-4 min-w-0">
                                                    <div className={`h-16 w-16 shrink-0 rounded-2xl bg-[#0f1115] border-2 border-${statusColor}-500/30 p-1 overflow-hidden shadow-lg`}>
                                                        {cityTheme.image ? (
                                                            <img src={cityTheme.image} className="w-full h-full object-cover rounded-xl" alt="City" />
                                                        ) : (
                                                            <div className="w-full h-full bg-gray-800 rounded-xl flex items-center justify-center text-gray-500 font-bold">{character.city[0]}</div>
                                                        )}
                                                    </div>
                                                    <div className="min-w-0 flex flex-col justify-center">
                                                        <h3 className="text-xl font-bold text-white leading-tight truncate pr-2" title={character.characterName || user.name}>
                                                            {character.characterName || user.name}
                                                        </h3>
                                                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                                                            <Badge variant="outline" className="bg-white/5 border-white/10 text-gray-400 text-[10px] tracking-wider font-mono whitespace-nowrap">
                                                                #{character.playerId || 'ID PENDENTE'}
                                                            </Badge>
                                                            <Badge className={`bg-${cityTheme.color}-500/10 text-${cityTheme.color}-400 border-${cityTheme.color}-500/20 text-[10px] whitespace-nowrap`}>
                                                                {character.city}
                                                            </Badge>
                                                        </div>
                                                    </div>
                                                </div>

                                            </div>

                                            {/* Highlighted ID and City */}
                                            <div className="flex flex-col gap-2">
                                                <div className="flex items-center justify-between bg-white/5 p-2 rounded-lg border border-white/5">
                                                    <span className="text-xs text-emerald-500 font-bold uppercase tracking-wider">Passaporte</span>
                                                    <span className="text-lg font-mono font-bold text-white tracking-widest">#{character.playerId || '???'}</span>
                                                </div>
                                                <div className={`flex items-center justify-between p-2 rounded-lg border bg-${cityTheme.color}-500/10 border-${cityTheme.color}-500/20`}>
                                                    <span className="text-xs font-bold uppercase tracking-wider text-white">Residência</span>
                                                    <span className={`text-sm font-bold text-${cityTheme.color}-400`}>{character.city}</span>
                                                </div>
                                            </div>

                                            {/* Stats Grid */}
                                            <div className="grid grid-cols-2 gap-3">
                                                <div className="bg-white/5 rounded-xl p-3 border border-white/5 flex flex-col justify-center">
                                                    <div className="flex items-center gap-2 text-gray-500 text-[10px] uppercase font-bold mb-1">
                                                        <Briefcase className="h-3 w-3" />
                                                        <span>Ocupação</span>
                                                    </div>
                                                    <span className="text-xs font-bold text-gray-200 truncate">{character.organization || 'Cidadão'}</span>
                                                    <span className="text-[10px] text-gray-500 mt-1 font-medium">Desde: {formatDate(character.joinedAt || user.joinDate)}</span>
                                                </div>

                                                {/* Expiration or Join Date */}
                                                {!isClean ? (
                                                    <div className={`bg-${statusColor}-500/10 rounded-xl p-3 border border-${statusColor}-500/20 flex flex-col justify-center`}>
                                                        <div className="flex items-center gap-2 text-red-500 text-[10px] uppercase font-bold mb-1">
                                                            <Clock className="h-3 w-3" />
                                                            <span>Expira em</span>
                                                        </div>
                                                        <span className="text-xs font-bold text-red-200">
                                                            {activePunishments[0].expiresAt ? formatDate(activePunishments[0].expiresAt, true) : 'Indefinido'}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <div className="bg-white/5 rounded-xl p-3 border border-white/5 flex flex-col justify-center">
                                                        <div className="flex items-center gap-2 text-gray-500 text-[10px] uppercase font-bold mb-1">
                                                            <Calendar className="h-3 w-3" />
                                                            <span>Registro</span>
                                                        </div>
                                                        <span className="text-xs font-bold text-gray-200">{formatDate(character.joinedAt || user.joinDate)}</span>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Punishments / Status Section */}
                                            <div className={`rounded-xl border bg-${statusColor}-500/5 border-${statusColor}-500/10 p-4 flex-1`}>
                                                <h4 className="text-xs font-bold uppercase tracking-widest mb-3 flex items-center gap-2 text-white">
                                                    {isClean ? <Activity className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
                                                    {isClean ? 'Status da Conta' : 'Punições e Restrições'}
                                                </h4>

                                                {isClean ? (
                                                    <div className="flex flex-col items-center justify-center py-4 text-center">
                                                        <div className="h-10 w-10 rounded-full bg-emerald-500/10 flex items-center justify-center mb-2">
                                                            <CheckCircle2 className="h-6 w-6 text-emerald-500" />
                                                        </div>
                                                        <p className="text-emerald-200 font-medium text-sm">Nenhuma restrição ativa</p>
                                                        <p className="text-emerald-500/50 text-xs mt-1">Sua conta está em dia com as regras da cidade.</p>
                                                    </div>
                                                ) : (
                                                    <div className="space-y-3">
                                                        {activePunishments.map((punishment) => (
                                                            <div key={punishment.id} className="group/punish bg-[#0f1115] border border-red-500/20 rounded-xl overflow-hidden hover:border-red-500/50 transition-all duration-300 shadow-[0_4px_20px_-10px_rgba(239,68,68,0.2)]">
                                                                {/* Punish Header / Chip */}
                                                                <div
                                                                    className="p-4 flex items-center justify-between cursor-pointer hover:bg-white/5 transition-colors relative overflow-hidden"
                                                                    onClick={() => setExpandedPunishmentId(expandedPunishmentId === punishment.id ? null : punishment.id)}
                                                                >
                                                                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-red-500" />

                                                                    <div className="flex items-center gap-3 pl-2">
                                                                        <div className={`p-2 rounded-lg bg-red-500/10 text-red-400 ring-1 ring-red-500/20`}>
                                                                            <Ban className="h-4 w-4" />
                                                                        </div>
                                                                        <div>
                                                                            <p className="text-sm font-bold text-white group-hover/punish:text-red-400 transition-colors">{punishment.punishmentType}</p>
                                                                            <div className="flex items-center gap-2 mt-0.5">
                                                                                {(() => {
                                                                                    const isUnderReview = punishment.status === 'under_review';
                                                                                    const isFinal = !!punishment.appealTicketId;

                                                                                    let label = 'ATIVO';
                                                                                    let colorClass = 'text-emerald-400';
                                                                                    let dotClass = 'bg-emerald-500';

                                                                                    if (isUnderReview) {
                                                                                        label = 'EM ANÁLISE';
                                                                                        colorClass = 'text-yellow-400';
                                                                                        dotClass = 'bg-yellow-500';
                                                                                    } else if (isFinal) {
                                                                                        label = 'APELAÇÃO NEGADA';
                                                                                        colorClass = 'text-red-500';
                                                                                        dotClass = 'bg-red-500';
                                                                                    }

                                                                                    return (
                                                                                        <>
                                                                                            <span className={`w-1.5 h-1.5 rounded-full ${dotClass} animate-pulse`} />
                                                                                            <p className={`text-[10px] ${colorClass} uppercase font-bold tracking-wider`}>
                                                                                                {label}
                                                                                            </p>
                                                                                        </>
                                                                                    );
                                                                                })()}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex items-center gap-2">
                                                                        {getAppealTicketId(punishment) && (
                                                                            <Button
                                                                                size="icon"
                                                                                variant="ghost"
                                                                                className="h-8 w-8 text-orange-400 hover:text-orange-300 hover:bg-orange-500/10 rounded-full"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    navigate(`/tickets/${getAppealTicketId(punishment)}`);
                                                                                }}
                                                                                title="Ver Ticket da Contestação"
                                                                            >
                                                                                <FileText className="h-4 w-4" />
                                                                            </Button>
                                                                        )}
                                                                        <div className="h-8 w-8 rounded-full bg-white/5 flex items-center justify-center border border-white/5 group-hover/punish:bg-white/10 group-hover/punish:border-white/10 transition-all">
                                                                            {expandedPunishmentId === punishment.id ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* Expanded Details */}
                                                                <AnimatePresence>
                                                                    {expandedPunishmentId === punishment.id && (
                                                                        <motion.div
                                                                            initial={{ height: 0 }}
                                                                            animate={{ height: 'auto' }}
                                                                            exit={{ height: 0 }}
                                                                            className="overflow-hidden border-t border-white/5 bg-black/20"
                                                                        >
                                                                            <div className="p-4 space-y-4 text-xs">
                                                                                {/* Status Explanation */}
                                                                                <div className="bg-white/5 rounded-lg p-3 border border-white/5">
                                                                                    {(() => {
                                                                                        const isUnderReview = punishment.status === 'under_review';
                                                                                        const isFinal = !!punishment.appealTicketId;

                                                                                        if (isUnderReview) {
                                                                                            return (
                                                                                                <div className="space-y-1">
                                                                                                    <p className="font-bold text-yellow-400 uppercase text-[10px]">Status: Em Análise</p>
                                                                                                    <p className="text-gray-400 leading-relaxed">Sua contestação foi recebida e está sendo revisada por um membro da Staff. Aguarde a resposta no ticket.</p>
                                                                                                </div>
                                                                                            );
                                                                                        } else if (isFinal) {
                                                                                            return (
                                                                                                <div className="space-y-1">
                                                                                                    <p className="font-bold text-red-500 uppercase text-[10px]">Status: Apelação Negada</p>
                                                                                                    <p className="text-gray-400 leading-relaxed">A Staff revisou seu recurso e decidiu manter a punição original. Esta é uma decisão final e não pode ser contestada novamente.</p>
                                                                                                </div>
                                                                                            );
                                                                                        } else {
                                                                                            return (
                                                                                                <div className="space-y-1">
                                                                                                    <p className="font-bold text-emerald-400 uppercase text-[10px]">Status: Ativo</p>
                                                                                                    <p className="text-gray-400 leading-relaxed">Esta punição está atualmente em vigor. Caso considere injusta, você pode iniciar um processo de contestação.</p>
                                                                                                </div>
                                                                                            );
                                                                                        }
                                                                                    })()}
                                                                                </div>

                                                                                <div className="grid gap-3">
                                                                                    <div className="flex flex-col gap-1 text-gray-400">
                                                                                        <span className="font-bold uppercase text-[9px] tracking-widest text-gray-500">Motivo:</span>
                                                                                        <span className="text-white font-medium text-sm bg-black/20 p-2 rounded border border-white/5 break-words">{punishment.publicReason}</span>
                                                                                    </div>
                                                                                    <div className="flex justify-between items-center text-gray-400 bg-white/5 p-2 rounded">
                                                                                        <span className="font-bold uppercase text-[9px] tracking-widest">Expiração:</span>
                                                                                        <span className="text-white font-medium">{punishment.expiresAt ? formatDate(punishment.expiresAt, true) : 'Indeterminado'}</span>
                                                                                    </div>
                                                                                </div>

                                                                                {/* Actions Area */}
                                                                                <div className="pt-2 flex gap-2">
                                                                                    {punishment.status === 'active' && !getAppealTicketId(punishment) ? (
                                                                                        <Button
                                                                                            size="sm"
                                                                                            className="w-full h-8 text-xs bg-red-500/20 hover:bg-red-500/30 text-red-200 border border-red-500/20"
                                                                                            onClick={() => {
                                                                                                setSelectedPunishment({
                                                                                                    ...punishment,
                                                                                                    characterName: character.characterName,
                                                                                                    characterId: character.playerId
                                                                                                });
                                                                                                setIsAppealModalOpen(true);
                                                                                            }}
                                                                                        >
                                                                                            Recorrer
                                                                                        </Button>
                                                                                    ) : getAppealTicketId(punishment) ? (
                                                                                        <Button
                                                                                            size="sm"
                                                                                            className="w-full h-8 text-xs bg-orange-500/20 hover:bg-orange-500/30 text-orange-200 border border-orange-500/20 gap-2"
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                navigate(`/tickets/${getAppealTicketId(punishment)}`);
                                                                                            }}
                                                                                        >
                                                                                            <FileText className="h-3 w-3" />
                                                                                            Ver Ticket
                                                                                        </Button>
                                                                                    ) : null}
                                                                                </div>
                                                                            </div>
                                                                        </motion.div>
                                                                    )}
                                                                </AnimatePresence>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Footer Actions */}
                                        {hasRevocations && (
                                            <div className="bg-white/5 border-t border-white/5 p-3 flex justify-center">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => {
                                                        setSelectedCharacterRevocations(revokedPunishments);
                                                        setIsRevokeHistoryOpen(true);
                                                    }}
                                                    className="text-xs text-gray-400 hover:text-white hover:bg-white/10 gap-2 h-8"
                                                >
                                                    <HistoryIcon className="h-3 w-3" />
                                                    Histórico ({revokedPunishments.length})
                                                </Button>
                                            </div>
                                        )}
                                    </Card>
                                </motion.div>
                            );
                        })
                ) : (
                    <div className="col-span-full flex flex-col items-center justify-center py-24 text-center bg-[#0f1115] rounded-[2rem] border border-white/5 relative overflow-hidden">
                        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5" />
                        <div className="bg-gray-800/30 rounded-full p-8 mb-6 relative z-10 ring-8 ring-gray-800/10">
                            <User className="h-16 w-16 text-gray-500" />
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-2 relative z-10">Nenhum personagem encontrado</h3>
                        <p className="text-gray-400 relative z-10 max-w-md">Não encontramos personagens vinculados à sua conta.</p>
                    </div>
                )}
            </motion.div>

            {/* Modals */}
            <PunishmentAppealModal
                punishment={selectedPunishment}
                isOpen={isAppealModalOpen}
                onClose={() => {
                    setIsAppealModalOpen(false);
                    setSelectedPunishment(null);
                }}
            />

            <RevocationHistoryModal
                isOpen={isRevokeHistoryOpen}
                onClose={() => setIsRevokeHistoryOpen(false)}
                revokedPunishments={selectedCharacterRevocations}
            />
        </div >
    );
};

export default UserCharacters;
